import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * @component EmailPreview
 * @description Renders HTML email content safely in an iframe with auto-resize
 * @param {Object} props
 * @param {string} props.htmlContent - HTML email content
 * @returns {JSX.Element}
 */
export function EmailPreview({ htmlContent }) {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState('auto');
  const timeoutRefs = useRef([]);

  // Function to calculate and set iframe height
  const resizeIframe = useCallback(() => {
    if (!iframeRef.current) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (!iframeDoc || !iframeDoc.body) return;

      // Get the actual content height
      const body = iframeDoc.body;
      const html = iframeDoc.documentElement;

      const height = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      );

      if (height > 0) {
        setIframeHeight(`${height}px`);
      }
    } catch (error) {
      // Cross-origin or other error
      console.warn('Could not access iframe content:', error);
    }
  }, []);

  // Resize when htmlContent changes
  useEffect(() => {
    if (!htmlContent || !iframeRef.current) return;

    // Reset height
    setIframeHeight('auto');

    // Clear any existing timeouts
    timeoutRefs.current.forEach(clearTimeout);
    timeoutRefs.current = [];

    let mutationObserver = null;

    // Wait for iframe to load, then resize
    const iframe = iframeRef.current;
    
    const handleLoad = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc || !iframeDoc.body) return;

        // Initial resize attempt
        resizeIframe();

        // Set up MutationObserver to detect DOM changes (images loading, content changes, etc.)
        if (typeof MutationObserver !== 'undefined') {
          mutationObserver = new MutationObserver(() => {
            resizeIframe();
          });

          mutationObserver.observe(iframeDoc.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
          });
        }

        // Wait for all images to load
        const images = iframeDoc.getElementsByTagName('img');
        let imagesLoaded = 0;
        const totalImages = images.length;

        if (totalImages > 0) {
          Array.from(images).forEach((img) => {
            if (img.complete) {
              imagesLoaded++;
            } else {
              img.onload = () => {
                imagesLoaded++;
                resizeIframe();
              };
              img.onerror = () => {
                imagesLoaded++;
                resizeIframe();
              };
            }
          });

          if (imagesLoaded === totalImages) {
            resizeIframe();
          }
        }

        // Try multiple times to account for async content (fonts, etc.)
        timeoutRefs.current = [
          setTimeout(() => resizeIframe(), 100),
          setTimeout(() => resizeIframe(), 500),
          setTimeout(() => resizeIframe(), 1000),
          setTimeout(() => resizeIframe(), 2000),
        ];
      } catch (error) {
        console.warn('Error setting up iframe resize:', error);
      }
    };

    iframe.addEventListener('load', handleLoad);
    
    // Also try immediately in case it's already loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      handleLoad();
    }

    return () => {
      iframe.removeEventListener('load', handleLoad);
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [htmlContent, resizeIframe]);

  if (!htmlContent) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        No email content available
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100%' }}>
      <iframe
        ref={iframeRef}
        srcDoc={htmlContent}
        title="Email Preview"
        style={{
          width: '100%',
          height: iframeHeight,
          minHeight: '100%',
          border: 'none',
          display: 'block',
        }}
        sandbox="allow-same-origin"
      />
    </Box>
  );
}

